import React, { useState, useEffect, useRef } from "react";
import useApi from "../../CustomHooks/useApi";
import { useSelector } from "react-redux";
import INDIAN_BANKS from "../../ResuableCompoment/indianBanks";

const LOGO_MAP = Object.fromEntries(INDIAN_BANKS.map((b) => [b.name, b.logo]));

const DepositRequest = () => {

    const { callApi: Cashinrequest } = useApi("/cashin/request", "POST");
    const { callApi: getBanks } = useApi("/admin/cashin/banks", "GET");

    const userId = useSelector((state) => state.auth.employeeDetails?.userId);

    const userRole = useSelector((state) => state.auth.employeeDetails?.role);

    const [banks, setBanks] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showPopup, setShowPopup] = useState(false);
    const [showBankDrop, setShowBankDrop] = useState(false);
    const bankDropRef = useRef(null);
    const [requestId, setRequestId] = useState("");
    const [message, setMessage] = useState("");
    const [isTermsAccepted, setIsTermsAccepted] = useState(false);
    const [showTermsDetails, setShowTermsDetails] = useState(false);

    const [formData, setFormData] = useState({
        depositMode: "",
        bank: "",
        depositDate: "",
        amount: "",
        remarks: "",
        proof: null
    });

    /* ================= FETCH BANKS ================= */

    useEffect(() => {

        const fetchBanks = async () => {

            try {

                const res = await getBanks();

                if (res?.data) {
                    setBanks(res.data);
                }

            } catch (error) {
                console.error("Bank API Error:", error);
            }

        };

        fetchBanks();

    }, []);

    /* ================= CLOSE BANK DROPDOWN ON OUTSIDE CLICK ================= */

    useEffect(() => {
        const handler = (e) => {
            if (bankDropRef.current && !bankDropRef.current.contains(e.target))
                setShowBankDrop(false);
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    /* ================= FILTER BANKS ================= */

    // const filteredBanks = banks.filter((bank) => {

    //     if (formData.depositMode === "CASH") return bank.bankType === "CASH";

    //     if (formData.depositMode === "CDM") return bank.bankType === "CDM";

    //     if (formData.depositMode === "ONLINE") return bank.bankType === "ONLINE";

    //     return false;

    // });
    const filteredBanks = banks.filter((bank) => {

        if (!bank.active) return false;

        if (formData.depositMode === "CASH") {
            return bank.bankType === "CASH";
        }

        if (formData.depositMode === "CDM") {
            return bank.bankType === "CDM";
        }

        if (formData.depositMode === "ONLINE") {
            return bank.bankType === "ONLINE";
        }

        if (formData.depositMode === "QR") {
            return bank.bankType === "QR";
        }

        return false;
    });

    // A-Z sort — logic unchanged, only display order
    const sortedBanks = [...filteredBanks].sort((a, b) =>
        (a.iin || a.name || "").localeCompare(b.iin || b.name || "")
    );

    const selectedBank = banks.find(
        (b) => String(b.bankId) === String(formData.bank)
    );

    /* ================= REMARK LABEL ================= */

    const getRemarksLabel = () => {

        if (formData.depositMode === "CASH") return "Branch Name / MID";

        if (formData.depositMode === "CDM") return "Branch / TXN Number";

        if (formData.depositMode === "ONLINE") return "UTR Number";

        if (formData.depositMode === "QR") return "UTR Number";

        return "Remarks";

    };

    /* ================= HANDLE INPUT ================= */

    const handleChange = (e) => {

        const { name, value, files } = e.target;

        if (files) {

            setFormData(prev => ({
                ...prev,
                proof: files[0]
            }));

            return;

        }

        if (name === "amount") {
            if (!/^\d*$/.test(value)) return;
        }

        setFormData(prev => ({
            ...prev,
            [name]: value,
            ...(name === "depositMode" ? { bank: "", remarks: "" } : {}),
            ...(name === "bank" ? { remarks: "" } : {})
        }));

    };

    const handleRemoveFile = () => {

        setFormData(prev => ({
            ...prev,
            proof: null
        }));

    };

    /* ================= SUBMIT ================= */

    const handleSubmit = async (e) => {

        e.preventDefault();

        try {

            setLoading(true);

            const selectedBank = banks.find(
                (b) => b.bankId === Number(formData.bank)
            );

            const form = new FormData();

            form.append("userId", userId);
            form.append("depositMode", formData.depositMode);
            form.append("bankCode", formData.bank);
            form.append("bankName", selectedBank?.name || "");
            form.append("amount", formData.amount);
            form.append("reEnterAmount", formData.amount);
            form.append("depositDate", formData.depositDate);
            form.append("remarks", formData.remarks);
            form.append("file", formData.proof);

            const res = await Cashinrequest(form);

            // 🔥 reset previous data
            setRequestId("");
            setMessage("");

            // ✅ Success case
            if (res?.status === "REQUESTED") {
                setRequestId(res.requestId);
                setMessage("Request Submitted Successfully");

            } else {
                setMessage(res?.message);
            }

            setShowPopup(true);

        } catch (error) {

            console.error("API Error:", error);

        } finally {

            setLoading(false);

        }

    };

    /* ================= RESET ================= */

    const resetForm = () => {

        setFormData({
            depositMode: "",
            bank: "",
            depositDate: "",
            amount: "",
            remarks: "",
            proof: null
        });

    };

    const handleOk = () => {

        setShowPopup(false);
        resetForm();

    };

    /* ================= VALIDATION ================= */

    const isFormValid =
        formData.depositMode &&
        formData.bank &&
        formData.depositDate &&
        formData.amount &&
        formData.remarks &&
        formData.proof &&
        isTermsAccepted;

    return (

        <div className="bg-[#EEF1F5] p-4 sm:p-6">

            {/* TITLE */}

            <div className="mb-6 sm:mb-8">

                <h1 className="text-2xl sm:text-3xl font-bold text-[#0A2A4F]">
                    Deposit Request
                </h1>

                <p className="text-gray-600 text-sm">
                    Submit your deposit request
                </p>

            </div>

            {/* FORM */}

            <div className="bg-white rounded-2xl shadow-md p-5 sm:p-8">

                <form onSubmit={handleSubmit} className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">

                    {/* MODE */}

                    <div>

                        <label className="text-sm font-semibold text-gray-700">
                            Deposit Mode
                        </label>

                        {/* <select
                            name="depositMode"
                            value={formData.depositMode}
                            onChange={handleChange}
                            className="w-full border rounded-lg px-3 py-2 mt-1"
                        >

                            <option value="">Select Mode</option>
                            <option value="CASH">Cash</option>
                            <option value="CDM">CDM</option>
                            <option value="ONLINE">Online (NEFT/RTGS/IMPS/UPI)</option>
                            <option value="QR">QR</option>

                        </select> */}
                        <select
    name="depositMode"
    value={formData.depositMode}
    onChange={handleChange}
    className="w-full border rounded-lg px-3 py-2 mt-1"
>

    <option value="">Select Mode</option>
    <option value="CASH">Cash</option>
    <option value="CDM">CDM</option>
    
    {userRole !== "MERCHANT" && (
        <>
            <option value="ONLINE">Online (NEFT/RTGS/IMPS/UPI)</option>
            <option value="QR">QR</option>
        </>
    )}

</select>

                    </div>

                    {/* BANK — custom dropdown with icon + A-Z sort */}

                    <div ref={bankDropRef} className="relative">

                        <label className="text-sm font-semibold text-gray-700">
                            Bank Account
                        </label>

                        {/* Trigger button */}
                        <button type="button"
                            onClick={() => setShowBankDrop((p) => !p)}
                            disabled={!formData.depositMode}
                            className={`w-full flex items-center gap-2 border rounded-lg px-3 py-2 mt-1 text-left transition ${
                                !formData.depositMode ? "bg-gray-50 cursor-not-allowed opacity-60" :
                                showBankDrop ? "border-[#0A2A4F] ring-2 ring-[#0A2A4F]/20 bg-white" : "bg-white border-gray-300"
                            }`}>
                            {selectedBank ? (
                                <>
                                    {LOGO_MAP[selectedBank.iin] ? (
                                        <img src={LOGO_MAP[selectedBank.iin]} alt={selectedBank.iin}
                                            className="w-6 h-6 rounded object-contain shrink-0"
                                            onError={(e) => { e.target.style.display = "none"; }} />
                                    ) : (
                                        <div className="w-6 h-6 rounded bg-[#0A2A4F] flex items-center justify-center text-white text-[9px] font-black shrink-0">
                                            {(selectedBank.iin || "B")[0]}
                                        </div>
                                    )}
                                    <span className="flex-1 text-sm font-semibold text-gray-800 truncate">
                                        {selectedBank.iin} — {selectedBank.accountNumber}
                                    </span>
                                </>
                            ) : (
                                <span className="flex-1 text-sm text-gray-400">Select Bank</span>
                            )}
                            <span className={`text-gray-400 text-xs transition-transform ${showBankDrop ? "rotate-180" : ""}`}>▼</span>
                        </button>

                        {/* Dropdown list */}
                        {showBankDrop && (
                            <div className="absolute z-50 top-full mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden max-h-60 overflow-y-auto">
                                {sortedBanks.length === 0 ? (
                                    <p className="text-xs text-gray-400 text-center py-4">No banks available</p>
                                ) : (
                                    sortedBanks.map((bank) => (
                                        <button key={bank.bankId} type="button"
                                            onClick={() => {
                                                setFormData((p) => ({ ...p, bank: String(bank.bankId), remarks: "" }));
                                                setShowBankDrop(false);
                                            }}
                                            className={`w-full flex items-center gap-3 px-3 py-2.5 hover:bg-[#0A2A4F]/5 transition text-left ${
                                                String(formData.bank) === String(bank.bankId) ? "bg-[#0A2A4F]/8" : ""
                                            }`}>
                                            {LOGO_MAP[bank.iin] ? (
                                                <img src={LOGO_MAP[bank.iin]} alt={bank.iin}
                                                    className="w-8 h-8 rounded-lg object-contain border border-gray-100 shrink-0"
                                                    onError={(e) => { e.target.style.display = "none"; e.target.nextSibling.style.display = "flex"; }} />
                                            ) : null}
                                            <div className={`w-8 h-8 rounded-lg bg-[#0A2A4F] items-center justify-center text-white text-[10px] font-black shrink-0 ${LOGO_MAP[bank.iin] ? "hidden" : "flex"}`}>
                                                {(bank.iin || "B")[0]}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-semibold text-gray-800 truncate">{bank.iin}</p>
                                                <p className="text-xs text-gray-600 font-mono font-semibold">{bank.accountNumber}</p>
                                            </div>
                                            {String(formData.bank) === String(bank.bankId) && (
                                                <span className="text-[#0A2A4F] text-sm shrink-0">✓</span>
                                            )}
                                        </button>
                                    ))
                                )}
                            </div>
                        )}

                    </div>

                    {/* DATE */}

                    <div>

                        <label className="text-sm font-semibold text-gray-700">
                            Deposit Date
                        </label>

                        <input
                            type="date"
                            name="depositDate"
                            value={formData.depositDate}
                            onChange={handleChange}
                            className="w-full border rounded-lg px-3 py-2 mt-1"
                        />

                    </div>

                    {/* AMOUNT */}

                    <div>

                        <label className="text-sm font-semibold text-gray-700">
                            Amount
                        </label>

                        <input
                            type="text"
                            name="amount"
                            value={formData.amount}
                            onChange={handleChange}
                            placeholder="Enter Amount"
                            className="w-full border rounded-lg px-3 py-2 mt-1"
                        />

                    </div>

                    {/* BRANCH / TXN / REMARKS */}

                    <div>

                        <label className="text-sm font-semibold text-gray-700">
                            Branch / TXN Number / Remarks
                        </label>

                        <p className="text-red-500 text-xs mt-1 mb-1">
                            Example: 12344566 your branch id or txid || put your remarks
                        </p>

                        <input
                            type="text"
                            name="remarks"
                            value={formData.remarks}
                            onChange={handleChange}
                            placeholder="e.g. 12344566 || put your remarks"
                            className="w-full border rounded-lg px-3 py-2 mt-1"
                        />

                    </div>

                    {/* FILE */}

                    <div className="sm:col-span-2">

                        <label className="text-sm font-semibold text-gray-700">
                            Deposit Proof
                        </label>

                        {!formData.proof ? (

                            <input
                                type="file"
                                name="proof"
                                onChange={handleChange}
                                className="w-full border rounded-lg px-3 py-2 mt-1"
                            />

                        ) : (

                            <div className="flex items-center gap-4 mt-2 bg-gray-100 p-3 rounded-lg">

                                <span className="text-sm text-gray-700">
                                    {formData.proof.name}
                                </span>

                                <a
                                    href={URL.createObjectURL(formData.proof)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-blue-600 text-sm font-medium"
                                >
                                    View
                                </a>

                                <button
                                    type="button"
                                    onClick={handleRemoveFile}
                                    className="text-red-500 text-sm font-medium"
                                >
                                    Cancel
                                </button>

                            </div>

                        )}

                    </div>

                    {/* DISCLAIMER CHECKBOX */}

                    <div className="sm:col-span-2">
                        <div className="flex items-center gap-2 bg-slate-50 p-3 rounded-xl border border-slate-200">
                            <input
                                type="checkbox"
                                id="terms"
                                checked={isTermsAccepted}
                                onChange={(e) => setIsTermsAccepted(e.target.checked)}
                                className="w-5 h-5 cursor-pointer accent-[#12345A]"
                            />
                            <label htmlFor="terms" className="text-sm text-slate-700 cursor-pointer select-none">
                                I have read, understood, and agree to BingoPay's Transactional{" "}
                                <button
                                    type="button"
                                    onClick={() => setShowTermsDetails(true)}
                                    className="text-indigo-600 font-bold hover:underline"
                                >
                                    Declaration and Disclaimer
                                </button>
                            </label>
                        </div>
                    </div>

                    {/* SUBMIT */}

                    <div className="sm:col-span-2">

                        <button
                            type="submit"
                            disabled={!isFormValid || loading}
                            className={`w-full py-3 rounded-lg font-semibold text-white flex items-center justify-center gap-2
              ${isFormValid && !loading
                                    ? "bg-[#0A2A4F] hover:bg-[#163c69]"
                                    : "bg-gray-400 cursor-not-allowed"
                                }`}
                        >

                            {loading ? (
                                <>
                                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                                    Processing...
                                </>
                            ) : (
                                "Proceed"
                            )}

                        </button>

                    </div>

                </form>

            </div>

            {/* SUCCESS POPUP */}

            {showPopup && (

                <div className="fixed inset-0 flex items-center justify-center bg-black/40 z-50">

                    <div className="bg-white rounded-xl p-6 w-[350px] text-center shadow-lg">
                        {requestId ? (
                            <>
                                <h2 className="text-lg font-semibold text-green-600 mb-2">
                                    {message}
                                </h2>

                                <p className="text-gray-700 text-sm mb-4">
                                    Request ID :
                                    <span className="font-semibold"> {requestId}</span>
                                </p>
                            </>
                        ) : (
                            <>
                                <h2 className="text-lg font-semibold text-red-600 mb-2">
                                    Failed
                                </h2>

                                <p className="text-gray-700 text-sm mb-4">
                                    {message}
                                </p>
                            </>
                        )}

                        <button
                            onClick={handleOk}
                            className="bg-[#0A2A4F] text-white px-6 py-2 rounded-lg hover:bg-[#163c69]"
                        >
                            OK
                        </button>

                    </div>

                </div>

            )}

            {/* DECLARATION AND DISCLAIMER MODAL */}
            {showTermsDetails && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
                    <div className="bg-white w-full max-w-5xl rounded-2xl shadow-2xl flex flex-col max-h-[90vh]">
                        <div className="p-4 border-b flex justify-between items-center bg-slate-50">
                            <h3 className="font-bold text-lg">TRANSACTIONAL DECLARATION</h3>
                            <button onClick={() => setShowTermsDetails(false)} className="text-2xl">&times;</button>
                        </div>

                        <div className="p-6 overflow-y-auto text-sm text-slate-600 leading-relaxed">
                            <p className="mb-4 font-bold text-slate-800">By ticking the checkbox below and proceeding with this payment, I (the "Distributor/Merchant") hereby confirm, declare, and agree to the following representations and warranties in favour of BingoPay (ShreeBalaJee Connect Pvt. Ltd.), unconditionally and without reservation, in relation to this transaction and each future transaction I conduct through BingoPay's platform:</p>
                            <ul className="list-decimal pl-5 space-y-2">
                                <li>I confirm and represent that the funds used or to be used in this transaction are not derived from, connected to, or intended for use in any money laundering activity, as defined under the Prevention of Money Laundering Act, 2002 ("PMLA") or any other applicable law or regulation in force in India or any other jurisdiction. I have not engaged in, and do not intend to engage in, any activity that constitutes money laundering under applicable law..</li>
                                <li>I confirm that the funds being used in this transaction are:
                                    (a) legitimately earned and/or obtained from a lawful source;
                                    (b) entirely my own funds, or funds over which I hold a valid and lawful authority or mandate to transact;
                                    (c) not sourced, directly or indirectly, from any criminal, illegal, fraudulent, or unauthorised activity; and
                                    (d) not the proceeds of any offence under applicable Indian law or the law of any other jurisdiction.</li>
                                <li>I confirm that all personal and transactional information provided in connection with this payment, including my identity, contact, billing, payment instrument details, and transaction purpose, is true, accurate, current, and complete. I acknowledge that providing any false, misleading, or incomplete information may constitute an offence under applicable Indian laws, including the Information Technology Act, 2000 and the Bharatiya Nyaya Sanhita, 2023, and may result in suspension or termination of access to BingoPay's platform and/or referral to law enforcement authorities.</li>
                                <li>I confirm that the purpose of this transaction is entirely lawful. The goods and/or services being purchased, or the purpose of payment being made, do not violate any applicable law, regulation, or governmental directive in India or any other jurisdiction. I am not using BingoPay's services to purchase, obtain, or facilitate access to any goods, services, or content that is prohibited, restricted, or illegal under applicable law.</li>
                                <li>I confirm that the funds being transmitted through this transaction are not connected to, and will not be used for, any act of terrorism, terrorist financing, or the support of any terrorist organisation or individual, as defined under the Unlawful Activities (Prevention) Act, 1967, the PMLA 2002, or any other applicable law. I am not acting on behalf of, or for the benefit of, any person or entity engaged in or associated with terrorist activity.</li>
                                <li>I confirm that; (a) I am an individual of 18 years of age or older, with full legal capacity and authority to enter into this Declaration, to make this payment, and to be bound by the terms hereof. If transacting on behalf of another person or entity, I hold a valid and subsisting authority (including, where required, a power of attorney or board resolution) to do so (b) the payment instrument (including debit card, credit card, prepaid card, UPI handle, net banking credentials, digital wallet, or any other mode of payment) used for this transaction is registered in my name and/or I am the duly authorised holder or user of such instrument. I am not using any stolen, compromised, fraudulently obtained, or unauthorised payment instrument, and I have not obtained or used any other person's payment instrument without their knowledge and consent.</li>
                                <li>I confirm that I am not: (a) a person or entity listed on any sanctions list maintained by the Government of India, the Reserve Bank of India, the United Nations Security Council, or any other competent authority; (b) resident in or transacting from a comprehensively sanctioned jurisdiction under Indian or international law; or (c) transacting for the direct or indirect benefit of any sanctioned person or entity.</li>
                                <li>I confirm that I am in compliance with all applicable laws, rules, and regulations, including the Payment and Settlement Systems Act, 2007, the Information Technology Act, 2000, the Consumer Protection Act, 2019, and all applicable RBI regulations in relation to this transaction.</li>
                                <li>I consent to BingoPay collecting, storing, using, and processing my personal and transactional data for: (a) processing this transaction; (b) complying with KYC, AML, CFT, and other legal and regulatory obligations, including applicable RBI directions; (c) fraud detection and risk assessment; and (d) reporting to competent authorities as required by law, in accordance with BingoPay's Privacy Policy and the Digital Personal Data Protection Act, 2023.</li>
                                <li>Where I authorise a recurring, standing, or mandate-based payment through BingoPay's platform (including debit card, credit card, prepaid instrument, or UPI auto-pay), I acknowledge that: (a) such authorisation is provided voluntarily and constitutes my express consent for recurring transactions under the RBI's Digital Payments – E-mandate Framework, 2026; (b) BingoPay and/or the acquirer bank will issue a pre-debit notification within the timeline prescribed by the RBI prior to each debit; (c) I may revoke or modify the mandate at any time through BingoPay's platform or my bank/issuer; and (d) recurring transactions are subject to mandate registration, applicable limits, and authentication requirements prescribed under the E-mandate Framework.</li>
                            </ul>
                            <p className="font-bold py-3 text-slate-800">
                                I acknowledge that BingoPay is a regulated payment aggregator subject to reporting obligations under the PMLA 2002 and the RBI (Regulation of Payment Aggregators) Directions, 2025 (effective September 15, 2025)I consent to BingoPay: (a) reporting my transaction to FIU-IND or any other competent regulatory or law enforcement authority if identified or reasonably suspected as a Suspicious Transaction or Reportable Transaction; (b) withholding, delaying, blocking, or reversing any payment pending verification or investigation; and (c) disclosing my transaction data and personal information to regulatory or law enforcement authorities to the extent required by law. BingoPay reserves the right to revise this Declaration at any time. The revised version shall be effective upon publication. Continued use of the platform after such revision constitutes acceptance of the updated Declaration.
                            </p>
                            <p className="font-bold py-3 text-slate-800">I acknowledge that BingoPay reserves the right, at its sole discretion and without prior notice or liability, to decline, delay, hold, freeze, or reverse any transaction -- including where: (a) BingoPay reasonably suspects a violation of this Declaration or applicable law; (b) the transaction triggers AML/CFT risk indicators; (c) a competent authority directs BingoPay to do so; or (d) BingoPay's fraud detection systems identify the transaction as suspicious or high-risk.</p>
                            <p className="font-bold mb-4 pt-3 text-slate-800">
                                (AS A CHECK BOX AND USERS CAN PROCEED ONLY AFTER SELECTING ALL)
                                I have read, understood, and agree to BingoPay's Transactional Declaration and Disclaimer. I confirm that: (i) the funds used in this transaction are from a legitimate and lawful source; (ii) this transaction is for a lawful purpose only; (iii) I am not engaged in any money laundering, terrorist financing, or other unlawful activity; (iv) all information provided by me is true, accurate, and complete; and (v) I am the authorised user of the payment instrument being used. I consent to BingoPay processing my transaction data and reporting to regulatory authorities as required by applicable law.
                            </p>
                        </div>

                        <div className="p-4 border-t bg-slate-50 flex justify-end">
                            <button
                                onClick={() => {
                                    setIsTermsAccepted(true);
                                    setShowTermsDetails(false);
                                }}
                                className="bg-[#12345A] text-white px-6 py-2 rounded-xl font-semibold"
                            >
                                I Understand & Accept
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

export default DepositRequest;